import _styled from "styled-components";
import { Badge as FlowBiteBadge } from 'flowbite-react';
import { useTranslation } from 'next-i18next';
import { useMemo } from 'react';
import { ClipBoardCopyIcon, CucinaIcon, ModalWrapperCloseIcon, PrivateModeProtectionIcon, formatDate, useModal, useResponsiveBreakPoints } from '@restworld/utils-common';
import { Title, SkeletonLoading, Paragraph, LinkBase, Button, BaseModal } from '../..';
import { ArrowRightIcon, BuildingOfficeIcon, ChatBubbleOvalLeftEllipsisIcon, CheckIcon, ClockIcon, FaceFrownIcon, FireIcon, InformationCircleIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export function JobApplicationCard(_ref) {
  var restaurantName = _ref.restaurantName,
      city = _ref.city,
      jobTitle = _ref.jobTitle,
      occupation = _ref.occupation,
      salaryMin = _ref.salaryMin,
      salaryMax = _ref.salaryMax,
      jobType = _ref.jobType,
      publishedAt = _ref.publishedAt,
      redirectUrl = _ref.redirectUrl,
      isLoading = _ref.isLoading,
      id = _ref.id,
      sentimentLabel = _ref.sentimentLabel,
      sentimentDescription = _ref.sentimentDescription,
      restaurantId = _ref.restaurantId,
      statusKey = _ref.statusKey,
      onRedirect = _ref.onRedirect,
      showRestaurantDetails = _ref.showRestaurantDetails,
      sentiment = _ref.sentiment,
      _ref$showSentimentInf = _ref.showSentimentInfo,
      showSentimentInfo = _ref$showSentimentInf === void 0 ? true : _ref$showSentimentInf,
      discardedByAI = _ref.discardedByAI,
      redirectWorkerToNewTab = _ref.redirectWorkerToNewTab;

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var hideRestaurantDetails = !showRestaurantDetails && !(sentiment === 'positive' && statusKey !== 'worker_jp_interested');
  return isLoading ? /*#__PURE__*/_jsx(SkeletonLoading, {
    variant: "job-positions-card"
  }) : /*#__PURE__*/_jsxs(CardWrapper, {
    id: id,
    children: [/*#__PURE__*/_jsxs(ContentWrapper, {
      children: [/*#__PURE__*/_jsxs(RestaurantHeaderDetails, {
        children: [/*#__PURE__*/_jsxs(RestaurantHeader, {
          children: [/*#__PURE__*/_jsxs(RestaurantInfo, {
            children: [!hideRestaurantDetails && /*#__PURE__*/_jsx(TitleWrapper, {
              title: restaurantName,
              icon: /*#__PURE__*/_jsx(_StyledBuildingOfficeIcon, {}),
              hidden: restaurantId ? true : false
            }), !hideRestaurantDetails && /*#__PURE__*/_jsx(TitleWrapper, {
              title: city,
              icon: /*#__PURE__*/_jsx(_StyledMapPinIcon, {})
            }), hideRestaurantDetails && /*#__PURE__*/_jsx(ParagraphLabelWithIcon, {
              icon: /*#__PURE__*/_jsx(PrivateModeProtectionIcon, {}),
              label: t('common:hidden_place')
            })]
          }), !!(!restaurantId ? jobTitle : restaurantName) && /*#__PURE__*/_jsx(Title, {
            textColor: "black",
            fontWeight: "semibold",
            standardVariant: "h5",
            children: !restaurantId ? jobTitle : restaurantName !== null && restaurantName !== void 0 ? restaurantName : t('common:name_hidden')
          })]
        }), sentimentLabel && /*#__PURE__*/_jsx(ApplicationStatusBadge, {
          discardedByAI: discardedByAI,
          statusKey: statusKey,
          restaurantId: restaurantId,
          children: sentimentLabel
        })]
      }), !restaurantId && /*#__PURE__*/_jsxs(JobInfoWrapper, {
        children: [occupation ? /*#__PURE__*/_jsx(Badge, {
          children: occupation
        }) : null, salaryMin && salaryMax ? /*#__PURE__*/_jsxs(Badge, {
          children: ["\u20AC", salaryMin, "-", salaryMax]
        }) : null, jobType ? /*#__PURE__*/_jsx(Badge, {
          children: jobType
        }) : null]
      }), (publishedAt || redirectUrl) && /*#__PURE__*/_jsxs(PublishLocationWrapper, {
        restaurantId: restaurantId,
        children: [/*#__PURE__*/_jsxs(Paragraph, {
          standardVariant: "body-sm",
          color: "gray-600",
          font: "normal",
          children: [t('common:sent_on'), " ", publishedAt ? formatDate(publishedAt) : '-']
        }), redirectUrl && /*#__PURE__*/_jsxs(LinkBase, {
          onLinkClick: function onLinkClick() {
            if (onRedirect) onRedirect();
          },
          link: redirectUrl,
          newTab: redirectWorkerToNewTab,
          twStyle: {
            "display": "flex",
            "width": "fit-content",
            "flexDirection": "row",
            "alignItems": "center",
            "gap": "0.375rem"
          },
          children: [/*#__PURE__*/_jsx(Paragraph, {
            variant: "small",
            font: "medium",
            color: "primary-700",
            children: t('common:details')
          }), /*#__PURE__*/_jsx(_StyledArrowRightIcon, {})]
        })]
      })]
    }), showSentimentInfo && (sentimentDescription || restaurantId) && /*#__PURE__*/_jsx(MoreInformation, {
      restaurantName: restaurantName,
      city: city,
      jobTitle: jobTitle,
      sentimentDescription: sentimentDescription,
      occupation: occupation,
      salaryMax: salaryMax,
      salaryMin: salaryMin,
      jobType: jobType,
      sentimentLabel: sentimentLabel,
      restaurantId: restaurantId,
      statusKey: statusKey
    })]
  });
}

function getStatusBadgeConfig(statusKey) {
  switch (statusKey) {
    case 'worker_jp_interested':
      return {
        style: {
          "--tw-bg-opacity": "1",
          "backgroundColor": "rgb(253 246 178 / var(--tw-bg-opacity))",
          "--tw-text-opacity": "1",
          "color": "rgb(142 75 16 / var(--tw-text-opacity))"
        },
        labelKey: 'common:application_received',
        icon: /*#__PURE__*/_jsx(_StyledClockIcon, {})
      };

    case 'worker_jp_viewed_by_employer':
    case 'worker_jp_in_shortlist':
      return {
        style: {
          "--tw-bg-opacity": "1",
          "backgroundColor": "rgb(253 246 178 / var(--tw-bg-opacity))",
          "--tw-text-opacity": "1",
          "color": "rgb(142 75 16 / var(--tw-text-opacity))"
        },
        labelKey: 'common:application_sent',
        icon: /*#__PURE__*/_jsx(_StyledCheckIcon, {})
      };

    case 'worker_jp_employer_interested':
      return {
        style: {
          "--tw-bg-opacity": "1",
          "backgroundColor": "rgb(253 246 178 / var(--tw-bg-opacity))",
          "--tw-text-opacity": "1",
          "color": "rgb(142 75 16 / var(--tw-text-opacity))"
        },
        labelKey: 'common:employer_interested',
        icon: /*#__PURE__*/_jsx(_StyledCheckIcon2, {})
      };

    case 'worker_jp_interview_scheduled':
    case 'worker_jp_interview_done':
      return {
        style: {
          "--tw-bg-opacity": "1",
          "backgroundColor": "rgb(245 213 176 / var(--tw-bg-opacity))",
          "--tw-text-opacity": "1",
          "color": "rgb(158 84 0 / var(--tw-text-opacity))"
        },
        labelKey: 'common:interview_scheduled',
        icon: /*#__PURE__*/_jsx(_StyledChatBubbleOvalLeftEllipsisIcon, {})
      };

    case 'worker_jp_stage_scheduled':
    case 'worker_jp_stage':
    case 'worker_jp_stage_done':
      return {
        style: {
          "--tw-bg-opacity": "1",
          "backgroundColor": "rgb(225 239 254 / var(--tw-bg-opacity))",
          "--tw-text-opacity": "1",
          "color": "rgb(30 66 159 / var(--tw-text-opacity))"
        },
        labelKey: 'common:trial_period',
        icon: /*#__PURE__*/_jsx(_StyledFireIcon, {})
      };

    case 'worker_jp_scheduled_hiring':
    case 'worker_jp_hired':
      return {
        style: {
          "--tw-bg-opacity": "1",
          "backgroundColor": "rgb(138 192 167 / var(--tw-bg-opacity))",
          "--tw-text-opacity": "1",
          "color": "rgb(0 65 35 / var(--tw-text-opacity))"
        },
        labelKey: 'common:hired',
        icon: /*#__PURE__*/_jsx(CucinaIcon, {
          size: 20,
          stroke: "#03543F"
        })
      };

    case 'worker_jp_discarded':
      return {
        style: {
          "--tw-bg-opacity": "1",
          "backgroundColor": "rgb(232 138 153 / var(--tw-bg-opacity))",
          "--tw-text-opacity": "1",
          "color": "rgb(112 0 19 / var(--tw-text-opacity))"
        },
        labelKey: 'common:not_fit'
      };

    case 'worker_jp_not_interested':
      return {
        style: {
          "--tw-bg-opacity": "1",
          "backgroundColor": "rgb(232 138 153 / var(--tw-bg-opacity))",
          "--tw-text-opacity": "1",
          "color": "rgb(112 0 19 / var(--tw-text-opacity))"
        },
        labelKey: 'common:rejected'
      };

    case 'worker_jp_proposed':
    case 'worker_jp_not_selected':
      return {
        style: {},
        labelKey: '',
        icon: undefined
      };

    default:
      return {
        style: {},
        labelKey: '',
        icon: undefined
      };
  }
}

export var ApplicationStatusBadge = function ApplicationStatusBadge(_ref2) {
  var statusKey = _ref2.statusKey,
      restaurantId = _ref2.restaurantId,
      discardedByAI = _ref2.discardedByAI;

  var _useTranslation2 = useTranslation(),
      t = _useTranslation2.t;

  var _useMemo = useMemo(function () {
    if (restaurantId) {
      return {
        badgeStyling: {
          "--tw-bg-opacity": "1",
          "backgroundColor": "rgb(246 245 255 / var(--tw-bg-opacity))",
          "--tw-text-opacity": "1",
          "color": "rgb(105 62 181 / var(--tw-text-opacity))"
        },
        badgeLabel: t('common:spontaneous_application'),
        badgeIcon: /*#__PURE__*/_jsx(ClipBoardCopyIcon, {
          size: 20,
          stroke: "#693EB5"
        })
      };
    }

    if (statusKey) {
      var badgeConfig = getStatusBadgeConfig(statusKey);
      return {
        badgeStyling: badgeConfig === null || badgeConfig === void 0 ? void 0 : badgeConfig.style,
        badgeLabel: discardedByAI ? t('job-application-page:profile_not_selected') : t(badgeConfig === null || badgeConfig === void 0 ? void 0 : badgeConfig.labelKey),
        badgeIcon: discardedByAI ? /*#__PURE__*/_jsx(_StyledFaceFrownIcon, {}) : badgeConfig === null || badgeConfig === void 0 ? void 0 : badgeConfig.icon
      };
    }

    return {
      badgeStyling: {},
      badgeLabel: '',
      badgeIcon: undefined
    };
  }, [discardedByAI, restaurantId, statusKey, t]),
      badgeStyling = _useMemo.badgeStyling,
      badgeLabel = _useMemo.badgeLabel,
      badgeIcon = _useMemo.badgeIcon;

  if (!badgeLabel) return null;
  return /*#__PURE__*/_jsx(StyledApplicationStatusBadge, {
    $twStyle: badgeStyling,
    children: /*#__PURE__*/_jsxs(_StyledDiv, {
      children: [badgeIcon, badgeLabel]
    })
  });
};
export default JobApplicationCard;

var CardWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "job-application-card__CardWrapper",
  componentId: "sc-1aoezz9-0"
})(function () {
  return [{
    "display": "flex",
    "width": "100%",
    "flexDirection": "column",
    "alignItems": "flex-start",
    "overflow": "hidden",
    "borderRadius": "0.938rem",
    "borderWidth": "1px",
    "--tw-border-opacity": "1",
    "borderColor": "rgb(209 213 219 / var(--tw-border-opacity))",
    "--tw-bg-opacity": "1",
    "backgroundColor": "rgb(255 255 255 / var(--tw-bg-opacity))",
    "@media (min-width: 1024px)": {
      "borderRadius": "20px",
      "--tw-border-opacity": "1",
      "borderColor": "rgb(229 231 235 / var(--tw-border-opacity))"
    }
  }];
});

var ContentWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "job-application-card__ContentWrapper",
  componentId: "sc-1aoezz9-1"
})(function () {
  return [{
    "display": "flex",
    "width": "100%",
    "flexDirection": "column",
    "alignItems": "flex-start",
    "padding": "1rem",
    "@media (min-width: 1024px)": {
      "padding": "1.5rem"
    }
  }];
});

var RestaurantHeaderDetails = /*#__PURE__*/_styled.div.withConfig({
  displayName: "job-application-card__RestaurantHeaderDetails",
  componentId: "sc-1aoezz9-2"
})(function () {
  return [{
    "display": "flex",
    "width": "100%",
    "flexDirection": "column-reverse",
    "alignItems": "flex-start",
    "justifyContent": "flex-start",
    "gap": "1rem",
    "@media (min-width: 1024px)": {
      "flexDirection": "row",
      "justifyContent": "space-between",
      "gap": "0px"
    }
  }];
});

var RestaurantHeader = /*#__PURE__*/_styled.div.withConfig({
  displayName: "job-application-card__RestaurantHeader",
  componentId: "sc-1aoezz9-3"
})(function () {
  return [{
    "display": "flex",
    "flexDirection": "column",
    "alignItems": "flex-start",
    "gap": "0.5rem"
  }];
});

var RestaurantInfo = /*#__PURE__*/_styled.div.withConfig({
  displayName: "job-application-card__RestaurantInfo",
  componentId: "sc-1aoezz9-4"
})(function () {
  return [{
    "display": "flex",
    "width": "fit-content",
    "flexDirection": "row",
    "alignItems": "flex-start",
    "gap": "1.5rem"
  }];
});

var PublishLocationWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "job-application-card__PublishLocationWrapper",
  componentId: "sc-1aoezz9-5"
})(["", ";", ""], {
  "display": "flex",
  "width": "100%",
  "flexDirection": "row",
  "alignItems": "center",
  "justifyContent": "space-between",
  "borderTopWidth": "1px",
  "--tw-border-opacity": "1",
  "borderColor": "rgb(209 213 219 / var(--tw-border-opacity))",
  "paddingTop": "1rem"
}, function (_ref3) {
  var restaurantId = _ref3.restaurantId;
  return restaurantId ? {
    "marginTop": "1rem"
  } : {};
});

var Badge = /*#__PURE__*/_styled(FlowBiteBadge).withConfig({
  displayName: "job-application-card__Badge",
  componentId: "sc-1aoezz9-6"
})(["", ";"], {
  "borderRadius": "0.5rem",
  "--tw-bg-opacity": "1",
  "backgroundColor": "rgb(246 245 255 / var(--tw-bg-opacity))",
  "paddingLeft": "0.5rem",
  "paddingRight": "0.5rem",
  "paddingTop": "0.25rem",
  "paddingBottom": "0.25rem",
  "fontSize": "0.875rem",
  "lineHeight": "1.313rem",
  "fontWeight": "400",
  "--tw-text-opacity": "1",
  "color": "rgb(75 85 99 / var(--tw-text-opacity))",
  "@media (min-width: 1024px)": {
    "fontSize": "0.875rem",
    "lineHeight": "1.313rem"
  }
});

var StyledApplicationStatusBadge = /*#__PURE__*/_styled(FlowBiteBadge).withConfig({
  displayName: "job-application-card__StyledApplicationStatusBadge",
  componentId: "sc-1aoezz9-7"
})(["", ";", ""], {
  "display": "flex",
  "flexDirection": "row",
  "alignItems": "center",
  "justifyContent": "flex-start",
  "borderRadius": "0.5rem",
  "--tw-bg-opacity": "1",
  "backgroundColor": "rgb(238 250 166 / var(--tw-bg-opacity))",
  "paddingLeft": "0.5rem",
  "paddingRight": "0.5rem",
  "paddingTop": "0.25rem",
  "paddingBottom": "0.25rem",
  "fontSize": "0.875rem",
  "lineHeight": "1.313rem",
  "fontWeight": "400",
  "textTransform": "uppercase",
  "--tw-text-opacity": "1",
  "color": "rgb(75 85 99 / var(--tw-text-opacity))",
  "@media (min-width: 1024px)": {
    "fontSize": "0.875rem",
    "lineHeight": "1.313rem"
  }
}, function (_ref4) {
  var $twStyle = _ref4.$twStyle;
  return $twStyle ? $twStyle : {};
});

var JobInfoWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "job-application-card__JobInfoWrapper",
  componentId: "sc-1aoezz9-8"
})(function () {
  return [{
    "marginTop": "1rem",
    "marginBottom": "1rem",
    "display": "flex",
    "width": "fit-content",
    "flexDirection": "row",
    "alignItems": "center",
    "justifyContent": "flex-start",
    "gap": "0.5rem"
  }];
});

var TitleWrapper = function TitleWrapper(_ref5) {
  var title = _ref5.title,
      icon = _ref5.icon,
      hidden = _ref5.hidden;

  var _useTranslation3 = useTranslation(),
      t = _useTranslation3.t;

  if (hidden) return null;
  return /*#__PURE__*/_jsxs(_StyledDiv2, {
    children: [icon, /*#__PURE__*/_jsx(Paragraph, {
      variant: "small",
      color: "black",
      font: "normal",
      children: title !== null && title !== void 0 ? title : t('common:name_hidden')
    })]
  });
};

export var ParagraphLabelWithIcon = function ParagraphLabelWithIcon(_ref6) {
  var icon = _ref6.icon,
      label = _ref6.label,
      maxChars = _ref6.maxChars;
  return /*#__PURE__*/_jsxs(_StyledDiv3, {
    children: [icon, /*#__PURE__*/_jsx(Paragraph, {
      standardVariant: "body-sm",
      color: "black",
      font: "normal",
      maxChars: maxChars,
      content: label
    })]
  });
};

var MoreInformationWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "job-application-card__MoreInformationWrapper",
  componentId: "sc-1aoezz9-9"
})(function (_ref7) {
  var hidden = _ref7.hidden;
  return [{
    "display": "flex",
    "width": "100%",
    "flexDirection": "column",
    "alignItems": "flex-start",
    "gap": "0.625rem",
    "--tw-bg-opacity": "1",
    "backgroundColor": "rgb(246 245 255 / var(--tw-bg-opacity))",
    "padding": "1rem",
    "@media (min-width: 1024px)": {
      "padding": "1.5rem"
    }
  }, hidden && {
    "display": "none"
  }];
});

var MoreInformation = function MoreInformation(_ref8) {
  var _sentimentDescription;

  var restaurantName = _ref8.restaurantName,
      city = _ref8.city,
      jobTitle = _ref8.jobTitle,
      statusKey = _ref8.statusKey,
      sentimentLabel = _ref8.sentimentLabel,
      occupation = _ref8.occupation,
      salaryMax = _ref8.salaryMax,
      salaryMin = _ref8.salaryMin,
      jobType = _ref8.jobType,
      sentimentDescription = _ref8.sentimentDescription,
      restaurantId = _ref8.restaurantId;

  var _useTranslation4 = useTranslation(),
      t = _useTranslation4.t;

  var _useResponsiveBreakPo = useResponsiveBreakPoints(),
      isDesktop = _useResponsiveBreakPo.isDesktop;

  var contentLength = (_sentimentDescription = sentimentDescription === null || sentimentDescription === void 0 ? void 0 : sentimentDescription.length) !== null && _sentimentDescription !== void 0 ? _sentimentDescription : 0;
  var limitContentLength = isDesktop ? 300 : 89;
  var hasCrossedLimit = contentLength > limitContentLength;
  var content = hasCrossedLimit ? ((sentimentDescription === null || sentimentDescription === void 0 ? void 0 : sentimentDescription.slice(0, limitContentLength)) || '') + '..' : sentimentDescription;
  return /*#__PURE__*/_jsxs(MoreInformationWrapper, {
    children: [/*#__PURE__*/_jsxs(_StyledDiv4, {
      children: [/*#__PURE__*/_jsx(_StyledInformationCircleIcon, {}), !restaurantId ? /*#__PURE__*/_jsx(Paragraph, {
        variant: "small",
        color: "gray-600",
        font: "normal",
        children: content
      }) : /*#__PURE__*/_jsxs(_StyledDiv5, {
        children: [/*#__PURE__*/_jsx(Paragraph, {
          variant: "small",
          font: "semibold",
          color: "gray-600",
          children: t('common:spontaneous_application')
        }), /*#__PURE__*/_jsx(Paragraph, {
          variant: "small",
          font: "normal",
          color: "gray-600",
          children: t('job-application-page:applications_not_traceable_by_restworld')
        })]
      })]
    }), hasCrossedLimit && !restaurantId && /*#__PURE__*/_jsx(ShowMoreInformationModal, {
      sentimentLabel: sentimentLabel,
      restaurantName: restaurantName,
      city: city,
      jobTitle: jobTitle,
      sentimentDescription: sentimentDescription,
      statusKey: statusKey,
      occupation: occupation,
      salaryMax: salaryMax,
      salaryMin: salaryMin,
      jobType: jobType,
      restaurantId: restaurantId
    })]
  });
};

var ShowMoreInformationModal = function ShowMoreInformationModal(_ref9) {
  var restaurantName = _ref9.restaurantName,
      city = _ref9.city,
      jobTitle = _ref9.jobTitle,
      sentimentLabel = _ref9.sentimentLabel,
      occupation = _ref9.occupation,
      salaryMax = _ref9.salaryMax,
      salaryMin = _ref9.salaryMin,
      jobType = _ref9.jobType,
      sentimentDescription = _ref9.sentimentDescription,
      restaurantId = _ref9.restaurantId,
      statusKey = _ref9.statusKey;

  var _useTranslation5 = useTranslation(),
      t = _useTranslation5.t;

  var _useModal = useModal(),
      close = _useModal.close,
      open = _useModal.open,
      show = _useModal.show;

  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(_StyledDiv6, {
      children: [/*#__PURE__*/_jsx(Button, {
        onClick: open,
        title: t('common:show_more'),
        variant: "ghost",
        suffixIcon: "PlusPurpleSmallIcon",
        fullwidth: false,
        fontColor: "primary",
        twStyle: {
          "width": "fit-content !important",
          "padding": "0px",
          "fontWeight": "500"
        }
      }), ' ']
    }), /*#__PURE__*/_jsxs(BaseModal, {
      onClose: close,
      showMobileGestures: true,
      open: show,
      modalStyles: [{
        "width": "100%",
        "@media (min-width: 768px)": {
          "minWidth": "22rem",
          "maxWidth": "26rem"
        }
      }],
      closeIcon: /*#__PURE__*/_jsx(ModalWrapperCloseIcon, {}),
      children: [/*#__PURE__*/_jsx(_StyledDiv7, {
        children: /*#__PURE__*/_jsx(ApplicationStatusBadge, {
          statusKey: statusKey,
          restaurantId: restaurantId,
          children: sentimentLabel
        })
      }), /*#__PURE__*/_jsx(_StyledDiv8, {
        children: /*#__PURE__*/_jsxs(RestaurantHeader, {
          children: [/*#__PURE__*/_jsxs(RestaurantInfo, {
            children: [/*#__PURE__*/_jsx(TitleWrapper, {
              title: restaurantName,
              icon: /*#__PURE__*/_jsx(_StyledBuildingOfficeIcon2, {})
            }), /*#__PURE__*/_jsx(TitleWrapper, {
              title: city,
              icon: /*#__PURE__*/_jsx(_StyledMapPinIcon2, {})
            })]
          }), /*#__PURE__*/_jsx(Title, {
            textColor: "black",
            fontWeight: "semibold",
            standardVariant: "h5",
            children: jobTitle
          })]
        })
      }), /*#__PURE__*/_jsxs(_StyledDiv9, {
        children: [occupation ? /*#__PURE__*/_jsx(Badge, {
          children: occupation
        }) : null, salaryMin && salaryMax ? /*#__PURE__*/_jsxs(Badge, {
          children: ["\u20AC", salaryMin, "-", salaryMax]
        }) : null, jobType ? /*#__PURE__*/_jsx(Badge, {
          children: jobType
        }) : null]
      }), /*#__PURE__*/_jsx(Paragraph, {
        variant: "small",
        color: "gray-500",
        font: "normal",
        twStyle: {
          "marginBottom": "1.5rem"
        },
        children: sentimentDescription
      }), /*#__PURE__*/_jsx(Button, {
        fullwidth: true,
        title: t('common:close'),
        onClick: close
      })]
    })]
  });
};

var _StyledBuildingOfficeIcon = /*#__PURE__*/_styled(BuildingOfficeIcon).withConfig({
  displayName: "job-application-card___StyledBuildingOfficeIcon",
  componentId: "sc-1aoezz9-10"
})({
  "height": "1.25rem",
  "width": "1.25rem",
  "--tw-text-opacity": "1",
  "color": "rgb(28 19 39 / var(--tw-text-opacity))"
});

var _StyledMapPinIcon = /*#__PURE__*/_styled(MapPinIcon).withConfig({
  displayName: "job-application-card___StyledMapPinIcon",
  componentId: "sc-1aoezz9-11"
})({
  "height": "1.25rem",
  "width": "1.25rem",
  "--tw-text-opacity": "1",
  "color": "rgb(28 19 39 / var(--tw-text-opacity))"
});

var _StyledArrowRightIcon = /*#__PURE__*/_styled(ArrowRightIcon).withConfig({
  displayName: "job-application-card___StyledArrowRightIcon",
  componentId: "sc-1aoezz9-12"
})({
  "height": "1.25rem",
  "width": "1.25rem",
  "--tw-text-opacity": "1",
  "color": "rgb(105 62 181 / var(--tw-text-opacity))"
});

var _StyledClockIcon = /*#__PURE__*/_styled(ClockIcon).withConfig({
  displayName: "job-application-card___StyledClockIcon",
  componentId: "sc-1aoezz9-13"
})({
  "height": "1.25rem",
  "width": "1.25rem",
  "--tw-text-opacity": "1",
  "color": "rgb(142 75 16 / var(--tw-text-opacity))"
});

var _StyledCheckIcon = /*#__PURE__*/_styled(CheckIcon).withConfig({
  displayName: "job-application-card___StyledCheckIcon",
  componentId: "sc-1aoezz9-14"
})({
  "height": "1.25rem",
  "width": "1.25rem",
  "--tw-text-opacity": "1",
  "color": "rgb(142 75 16 / var(--tw-text-opacity))"
});

var _StyledCheckIcon2 = /*#__PURE__*/_styled(CheckIcon).withConfig({
  displayName: "job-application-card___StyledCheckIcon2",
  componentId: "sc-1aoezz9-15"
})({
  "height": "1.25rem",
  "width": "1.25rem",
  "--tw-text-opacity": "1",
  "color": "rgb(142 75 16 / var(--tw-text-opacity))"
});

var _StyledChatBubbleOvalLeftEllipsisIcon = /*#__PURE__*/_styled(ChatBubbleOvalLeftEllipsisIcon).withConfig({
  displayName: "job-application-card___StyledChatBubbleOvalLeftEllipsisIcon",
  componentId: "sc-1aoezz9-16"
})({
  "height": "1.25rem",
  "width": "1.25rem",
  "--tw-text-opacity": "1",
  "color": "rgb(158 84 0 / var(--tw-text-opacity))"
});

var _StyledFireIcon = /*#__PURE__*/_styled(FireIcon).withConfig({
  displayName: "job-application-card___StyledFireIcon",
  componentId: "sc-1aoezz9-17"
})({
  "height": "1.25rem",
  "width": "1.25rem",
  "--tw-text-opacity": "1",
  "color": "rgb(30 66 159 / var(--tw-text-opacity))"
});

var _StyledFaceFrownIcon = /*#__PURE__*/_styled(FaceFrownIcon).withConfig({
  displayName: "job-application-card___StyledFaceFrownIcon",
  componentId: "sc-1aoezz9-18"
})({
  "height": "1.25rem",
  "width": "1.25rem",
  "--tw-text-opacity": "1",
  "color": "rgb(145 0 24 / var(--tw-text-opacity))"
});

var _StyledDiv = /*#__PURE__*/_styled("div").withConfig({
  displayName: "job-application-card___StyledDiv",
  componentId: "sc-1aoezz9-19"
})({
  "display": "flex",
  "flexDirection": "row",
  "flexWrap": "nowrap",
  "alignItems": "center",
  "gap": "0.375rem"
});

var _StyledDiv2 = /*#__PURE__*/_styled("div").withConfig({
  displayName: "job-application-card___StyledDiv2",
  componentId: "sc-1aoezz9-20"
})({
  "display": "flex",
  "flexDirection": "row",
  "alignItems": "center",
  "gap": "0.5rem"
});

var _StyledDiv3 = /*#__PURE__*/_styled("div").withConfig({
  displayName: "job-application-card___StyledDiv3",
  componentId: "sc-1aoezz9-21"
})({
  "display": "flex",
  "flexDirection": "row",
  "alignItems": "center",
  "gap": "0.5rem"
});

var _StyledDiv4 = /*#__PURE__*/_styled("div").withConfig({
  displayName: "job-application-card___StyledDiv4",
  componentId: "sc-1aoezz9-22"
})({
  "display": "flex",
  "width": "100%",
  "flexDirection": "row",
  "alignItems": "flex-start",
  "gap": "0.5rem"
});

var _StyledInformationCircleIcon = /*#__PURE__*/_styled(InformationCircleIcon).withConfig({
  displayName: "job-application-card___StyledInformationCircleIcon",
  componentId: "sc-1aoezz9-23"
})({
  "height": "1.25rem",
  "width": "1.25rem",
  "minWidth": "20px",
  "--tw-text-opacity": "1",
  "color": "rgb(75 85 99 / var(--tw-text-opacity))"
});

var _StyledDiv5 = /*#__PURE__*/_styled("div").withConfig({
  displayName: "job-application-card___StyledDiv5",
  componentId: "sc-1aoezz9-24"
})({
  "display": "flex",
  "flexDirection": "column",
  "alignItems": "flex-start",
  "gap": "0.25rem"
});

var _StyledDiv6 = /*#__PURE__*/_styled("div").withConfig({
  displayName: "job-application-card___StyledDiv6",
  componentId: "sc-1aoezz9-25"
})({
  "display": "flex",
  "width": "100%",
  "flexDirection": "row",
  "alignItems": "flex-start",
  "justifyContent": "flex-start"
});

var _StyledDiv7 = /*#__PURE__*/_styled("div").withConfig({
  displayName: "job-application-card___StyledDiv7",
  componentId: "sc-1aoezz9-26"
})({
  "marginBottom": "1.5rem",
  "display": "flex",
  "width": "100%",
  "flexDirection": "row",
  "alignItems": "flex-start"
});

var _StyledDiv8 = /*#__PURE__*/_styled("div").withConfig({
  displayName: "job-application-card___StyledDiv8",
  componentId: "sc-1aoezz9-27"
})({
  "display": "flex",
  "width": "100%",
  "flexDirection": "column",
  "alignItems": "flex-start",
  "gap": "1rem"
});

var _StyledBuildingOfficeIcon2 = /*#__PURE__*/_styled(BuildingOfficeIcon).withConfig({
  displayName: "job-application-card___StyledBuildingOfficeIcon2",
  componentId: "sc-1aoezz9-28"
})({
  "height": "1.25rem",
  "width": "1.25rem",
  "--tw-text-opacity": "1",
  "color": "rgb(28 19 39 / var(--tw-text-opacity))"
});

var _StyledMapPinIcon2 = /*#__PURE__*/_styled(MapPinIcon).withConfig({
  displayName: "job-application-card___StyledMapPinIcon2",
  componentId: "sc-1aoezz9-29"
})({
  "height": "1.25rem",
  "width": "1.25rem",
  "--tw-text-opacity": "1",
  "color": "rgb(28 19 39 / var(--tw-text-opacity))"
});

var _StyledDiv9 = /*#__PURE__*/_styled("div").withConfig({
  displayName: "job-application-card___StyledDiv9",
  componentId: "sc-1aoezz9-30"
})({
  "marginTop": "1.5rem",
  "marginBottom": "1.5rem",
  "display": "flex",
  "width": "fit-content",
  "flexDirection": "row",
  "alignItems": "center",
  "justifyContent": "flex-start",
  "gap": "0.5rem"
});